import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus, Search, Route as RouteIcon, Sparkles, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import AIInquiryAssistant from "@/components/AIInquiryAssistant";

const STATUS_STYLES = {
  draft: "bg-slate-100 text-slate-500 dark:bg-ink-panel dark:text-slate-400",
  active: "bg-sky-50 text-sky-700 dark:bg-ink-gold/10 dark:text-ink-gold",
  converted: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  cancelled: "bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
};
const INPUT = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-ink-line dark:bg-ink-panel dark:text-ink-text";
const LABEL = "mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400";

export default function Inquiries() {
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("inquiries.create");
  const canAI = hasPermission("ai.requirements.extract");
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [modal, setModal] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  const load = async () => {
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (statusFilter) params.status = statusFilter;
      const r = await api.get("/inquiries", { params });
      setRows(r.data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, statusFilter]); // eslint-disable-line

  const openCreate = async () => {
    try {
      const r = await api.get("/customers", { params: { status: "active" } });
      setCustomers(r.data);
      setModal(true);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="inquiries-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-slate-400">Sales Pipeline</p>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-ink-text">Inquiries</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Travel inquiries and trip requirements, ready for costing.</p>
        </div>
        <div className="flex items-center gap-2">
          {canAI && (
            <button onClick={() => setAiOpen(true)} data-testid="draft-with-ai-button" className="flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 dark:border-ink-line dark:text-slate-300 dark:hover:bg-ink-panel">
              <Sparkles className="h-4 w-4" /> Draft with AI
            </button>
          )}
          {canCreate && (
            <button onClick={openCreate} data-testid="add-inquiry-button" className="flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-500 dark:bg-ink-gold dark:text-[#0B1120] dark:hover:bg-ink-golddeep">
              <Plus className="h-4 w-4" /> New inquiry
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-56 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search number, destination, trip type…" data-testid="inquiry-search-input" className={`${INPUT} pl-9`} />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="inquiry-status-filter" className={`${INPUT} w-auto`}>
          <option value="">All statuses</option>
          {["draft", "active", "converted", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-sky-600 dark:text-ink-gold" /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center dark:border-ink-line" data-testid="inquiries-empty-state">
          <RouteIcon className="mx-auto h-8 w-8 text-slate-400" />
          <p className="mt-3 text-sm font-medium text-slate-600 dark:text-slate-300">No inquiries yet</p>
          <p className="mt-1 text-xs text-slate-400">Create an inquiry from a customer to start building a trip.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-ink-line dark:bg-ink-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="inquiries-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500 dark:border-ink-line dark:bg-ink/40">
                  <th className="px-5 py-3 font-medium">Inquiry #</th>
                  <th className="px-5 py-3 font-medium">Customer</th>
                  <th className="px-5 py-3 font-medium">Destinations</th>
                  <th className="px-5 py-3 font-medium">Travel dates</th>
                  <th className="px-5 py-3 font-medium">Pax</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((i) => (
                  <tr key={i.id} onClick={() => navigate(`/inquiries/${i.id}`)} className="cursor-pointer border-b border-slate-100 last:border-0 hover:bg-slate-50 dark:border-ink-line dark:hover:bg-ink-panel/40" data-testid={`inquiry-row-${i.inquiry_number}`}>
                    <td className="px-5 py-3 font-mono-tf text-xs font-semibold text-slate-700 dark:text-ink-gold">{i.inquiry_number}</td>
                    <td className="px-5 py-3 font-medium text-slate-800 dark:text-ink-text">{i.customer_name || "—"}</td>
                    <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{(i.destinations || []).join(", ") || "—"}</td>
                    <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{i.travel_start_date ? `${String(i.travel_start_date).slice(0,10)} → ${String(i.travel_end_date||"").slice(0,10)}` : "—"}</td>
                    <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{i.num_travelers}</td>
                    <td className="px-5 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLES[i.status] || STATUS_STYLES.draft}`} data-testid={`inquiry-status-${i.inquiry_number}`}>{i.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modal && <InquiryModal customers={customers} onClose={() => setModal(false)} onSaved={(id) => navigate(`/inquiries/${id}`)} />}
      {aiOpen && <AIInquiryAssistant onClose={() => setAiOpen(false)} onSaved={(id) => navigate(`/inquiries/${id}`)} />}
    </div>
  );
}

function InquiryModal({ customers, onClose, onSaved }) {
  const [form, setForm] = useState({ customer_id: "", status: "draft", origin: "", destinations: "", travel_start_date: "", travel_end_date: "", num_travelers: 1, trip_type: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_id) return toast.error("Select a customer");
    setSaving(true);
    try {
      const payload = {
        customer_id: form.customer_id, status: form.status,
        origin: form.origin || undefined, trip_type: form.trip_type || undefined,
        travel_start_date: form.travel_start_date || undefined, travel_end_date: form.travel_end_date || undefined,
        num_travelers: Number(form.num_travelers) || 1, notes: form.notes || undefined,
        destinations: form.destinations ? form.destinations.split(",").map((d) => d.trim()).filter(Boolean) : [],
      };
      const r = await api.post("/inquiries", payload);
      toast.success(`Inquiry ${r.data.inquiry_number} created`);
      onSaved(r.data.id);
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  const cname = (c) => c.brand_name || c.preferred_name || c.legal_name || c.full_legal_name || c.customer_id;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="inquiry-modal">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-xl border border-slate-200 bg-white shadow-2xl dark:border-ink-line dark:bg-ink-card">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4 dark:border-ink-line">
          <h3 className="font-display text-lg font-semibold text-slate-900 dark:text-ink-text">New Inquiry</h3>
          <button onClick={onClose} data-testid="inquiry-modal-close" className="rounded-md p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-ink-panel"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={submit} className="max-h-[70vh] space-y-4 overflow-y-auto px-6 py-5">
          <div>
            <label className={LABEL}>Customer *</label>
            <select required value={form.customer_id} onChange={set("customer_id")} data-testid="inquiry-customer-select" className={INPUT}>
              <option value="">Select a customer…</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.customer_id} · {cname(c)} ({c.customer_type})</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={LABEL}>Origin</label><input value={form.origin} onChange={set("origin")} data-testid="inquiry-origin-input" className={INPUT} /></div>
            <div><label className={LABEL}>Trip Type</label><input value={form.trip_type} onChange={set("trip_type")} placeholder="FIT / Group / Honeymoon" data-testid="inquiry-trip-type-input" className={INPUT} /></div>
          </div>
          <div><label className={LABEL}>Destinations (comma-separated)</label><input value={form.destinations} onChange={set("destinations")} placeholder="Bali, Komodo" data-testid="inquiry-destinations-input" className={INPUT} /></div>
          <div className="grid grid-cols-3 gap-4">
            <div><label className={LABEL}>Start</label><input type="date" value={form.travel_start_date} onChange={set("travel_start_date")} data-testid="inquiry-start-input" className={INPUT} /></div>
            <div><label className={LABEL}>End</label><input type="date" value={form.travel_end_date} onChange={set("travel_end_date")} data-testid="inquiry-end-input" className={INPUT} /></div>
            <div><label className={LABEL}>Travelers</label><input type="number" min="1" value={form.num_travelers} onChange={set("num_travelers")} data-testid="inquiry-pax-input" className={INPUT} /></div>
          </div>
          <div><label className={LABEL}>Notes</label><textarea rows={2} value={form.notes} onChange={set("notes")} data-testid="inquiry-notes-input" className={INPUT} /></div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 dark:border-ink-line dark:text-slate-300">Cancel</button>
            <button type="submit" disabled={saving} data-testid="inquiry-modal-save" className="flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-500 disabled:opacity-60 dark:bg-ink-gold dark:text-[#0B1120] dark:hover:bg-ink-golddeep">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
